import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import {
  connectFood99Store,
  disconnectFood99Store,
  getFood99AuthorizationPage,
  listFood99Stores,
  setFood99ConfirmMethod,
} from "../../../api/DeliveryCenter/food99";

/**
 * 99Food store binding.
 *
 * Three steps, which is why this is a list of stores rather than a single switch:
 *
 *   1. We generate an authorization page URL (app-wide, valid 7 days).
 *   2. The 99Food super-admin opens it and clicks "Autorizar" on their stores. That only
 *      *authorizes* our app — the page only lists stores where that login is
 *      super-admin, and nothing is bound yet.
 *   3. "Conectar" binds one store: it sends 99Food's store id, typed here (pre-filled
 *      when we already know it), and the server binds it with this restaurant's id and
 *      caches its token, so the first real order does not spend its 5-minute
 *      confirmation window on a rate-limited token call.
 *
 * The id is typed rather than picked from a list because 99Food's list of authorized
 * stores spans every Slapie account.
 */
const Food99StoresModal = ({ show, closeModal }) => {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [stores, setStores] = useState([]);
  const [authUrl, setAuthUrl] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [shopIds, setShopIds] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await listFood99Stores();
      setConfigured(data.configured);
      setStores(data.stores || []);
      setShopIds(
        Object.fromEntries(
          (data.stores || []).map((s) => [s.id, s.food99_shop_id || ""])
        )
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Não foi possível carregar as lojas do 99Food."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const handleGenerateUrl = async () => {
    setError("");
    setNotice("");
    try {
      const { data } = await getFood99AuthorizationPage();
      setAuthUrl(data.url);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Não foi possível gerar o link de autorização."
      );
    }
  };

  const handleConnect = async (store) => {
    setBusyId(store.id);
    setError("");
    setNotice("");
    try {
      const shopId = (shopIds[store.id] || "").trim();
      await connectFood99Store(store.id, store.bound ? undefined : shopId);
      setNotice(`${store.name} conectada ao 99Food.`);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Não foi possível verificar a conexão desta loja."
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleDisconnect = async (store) => {
    setBusyId(store.id);
    setError("");
    setNotice("");
    try {
      await disconnectFood99Store(store.id);
      setNotice(`${store.name} desconectada.`);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Não foi possível desconectar a loja."
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmMethod = async (store, openapiOnly) => {
    setBusyId(store.id);
    setError("");
    setNotice("");
    try {
      await setFood99ConfirmMethod(store.id, openapiOnly);
      setNotice(
        openapiOnly
          ? `${store.name}: pedidos agora só podem ser confirmados pelo Slapie.`
          : `${store.name}: pedidos podem ser confirmados pelo Slapie ou pelo app do 99Food.`
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Não foi possível alterar o modo de confirmação."
      );
    } finally {
      setBusyId(null);
    }
  };

  const copy = (value) => navigator.clipboard?.writeText(String(value));

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="90%"
      maxWidth="820px"
    >
      <ModalHeader
        title="99Food"
        icon={StorefrontIcon}
        onClose={closeModal}
      />
      <ModalContent>
        {!configured && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            A integração com o 99Food ainda não foi configurada neste ambiente.
            Fale com o suporte para cadastrar as credenciais do aplicativo.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
            {notice}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Pedidos do 99Food precisam ser confirmados em até{" "}
          <strong>5 minutos</strong>, senão a própria plataforma cancela o
          pedido. Ative o Piloto Automático para que a confirmação seja enviada
          assim que o pedido chegar.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1. Link de autorização
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 1.5 }}
          >
            Gere o link e envie para o responsável pela loja no 99Food. Ele vale
            por 7 dias. Ao abrir, o super-administrador clica em{" "}
            <strong>Autorizar</strong> em cada loja — a página só mostra as lojas
            em que aquele login é super-administrador. Depois, conecte cada loja
            abaixo.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={handleGenerateUrl}>
              Gerar link
            </Button>
            {authUrl && (
              <>
                <Tooltip title="Copiar link">
                  <IconButton onClick={() => copy(authUrl)} size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Link
                  href={authUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  Abrir <OpenInNewIcon fontSize="inherit" />
                </Link>
              </>
            )}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2">2. Suas lojas</Typography>
          <Tooltip title="Atualizar">
            <IconButton onClick={load} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : stores.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, py: 2 }}
          >
            Nenhuma loja cadastrada nesta central.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {stores.map((store) => (
              <Box
                key={store.id}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius / 4 || 1,
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="subtitle2">{store.name}</Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ mt: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        app_shop_id: <strong>{store.app_shop_id}</strong>
                      </Typography>
                      <Tooltip title="Copiar app_shop_id">
                        <IconButton
                          size="small"
                          onClick={() => copy(store.app_shop_id)}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <Chip
                    size="small"
                    label={store.bound ? "Conectada" : "Não conectada"}
                    color={store.bound ? "success" : "default"}
                    variant={store.bound ? "filled" : "outlined"}
                  />
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  {!store.bound && (
                    <TextField
                      size="small"
                      label="ID da loja no 99Food"
                      value={shopIds[store.id] || ""}
                      onChange={(e) =>
                        setShopIds((prev) => ({
                          ...prev,
                          [store.id]: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      inputProps={{ inputMode: "numeric" }}
                      sx={{ minWidth: 240 }}
                    />
                  )}
                  <Button
                    size="small"
                    variant={store.bound ? "outlined" : "contained"}
                    disabled={
                      busyId === store.id ||
                      (!store.bound && !(shopIds[store.id] || "").trim())
                    }
                    onClick={() => handleConnect(store)}
                  >
                    {busyId === store.id ? (
                      <CircularProgress size={16} />
                    ) : store.bound ? (
                      "Revalidar"
                    ) : (
                      "Conectar"
                    )}
                  </Button>

                  {store.bound && (
                    <>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<LinkOffIcon />}
                        disabled={busyId === store.id}
                        onClick={() => handleDisconnect(store)}
                      >
                        Desconectar
                      </Button>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ ml: "auto" }}
                      >
                        <Typography variant="caption">
                          Confirmar só pelo Slapie
                        </Typography>
                        <Switch
                          size="small"
                          disabled={busyId === store.id}
                          onChange={(e) =>
                            handleConfirmMethod(store, e.target.checked)
                          }
                        />
                      </Stack>
                    </>
                  )}
                </Stack>

                {store.bound && store.food99_shop_id && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: "block",
                      mt: 1,
                    }}
                  >
                    shop_id no 99Food: {store.food99_shop_id}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </ModalContent>
    </AnimatedModal>
  );
};

export default Food99StoresModal;
